// ---------------------------------------------------------------------------
// ApprovalQueue — CMS-03a TCM Admin review queue
// ---------------------------------------------------------------------------
// Lists templates currently in the signed-in reviewer's queue (per
// REVIEW_QUEUE_STATUSES for the role). Clicking "Review" routes to the
// existing TemplateReview page, which already exposes the transition
// actions (approve / request revisions / send to HP) via TransitionButtons.
//
// The list refetches every 30s (locked decision 2026-05-28). For TCM Admin
// the queue covers pending_internal_review + revision_requested per the
// walked decision so the admin can decide whether to self-edit or push
// back to TCM Staff via "Re-open as draft" from the Review page.
//
// Health Plan reviewers (role = read_only → 'Health Plan') see their own
// queue (pending_client_review) IF accessibleRolesCode is populated. That
// path is CMS-03b and is gated on the trigger-resolver follow-up.

import { Link } from 'react-router';
import { Eye, Inbox } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useApprovalQueue } from '@/hooks/use-workflow';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, statusLabel } from '@/lib/utils';
import { REVIEW_QUEUE_STATUSES } from '@/types/workflow';
import type { TemplateStatus } from '@/types/template';
import { useOrgNodes } from '@/hooks/use-org-nodes';

const STATUS_BADGE: Record<
  TemplateStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive'
> = {
  draft: 'secondary',
  pending_internal_review: 'default',
  tcm_admin_approved: 'default',
  pending_client_review: 'warning',
  live: 'success',
  revision_requested: 'destructive',
};

const TYPE_LABEL: Record<string, string> = {
  scheduled_education: 'Scheduled Education',
  biometric_triggered: 'Biometric Triggered',
  adherence: 'Adherence',
  patient_satisfaction: 'Patient Satisfaction',
};

export default function ApprovalQueue() {
  const { user } = useAuth();
  const { data, isLoading, error } = useApprovalQueue(user?.role);
  const { data: orgNodes } = useOrgNodes();

  const queueStatuses = (user && REVIEW_QUEUE_STATUSES[user.role]) ?? [];
  const hasReviewQueue = queueStatuses.length > 0;

  if (!user) {
    return null;
  }

  if (!hasReviewQueue) {
    return (
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-carbon-900">Approvals</h2>
        <div className="rounded-ds-008 border border-silver-200 bg-white p-8 text-center">
          <Inbox className="mx-auto h-8 w-8 text-carbon-300" />
          <p className="mt-3 text-sm text-carbon-500">
            No review queue available for your role.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h2 className="text-xl font-semibold text-carbon-900">Approvals</h2>
        <p className="text-sm text-carbon-500">
          Templates awaiting your review
          {data && (
            <span className="ml-1 text-carbon-400">
              ({data.totalCount} total)
            </span>
          )}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : error ? (
        <div className="p-8 text-center text-red-600">
          Failed to load the approval queue.
        </div>
      ) : !data || data.templates.length === 0 ? (
        <div className="rounded-ds-008 border border-silver-200 bg-white p-12 text-center">
          <Inbox className="mx-auto h-10 w-10 text-carbon-300" />
          <p className="mt-3 text-sm font-medium text-carbon-700">
            Nothing pending your review right now.
          </p>
          <p className="mt-1 text-xs text-carbon-500">
            New items appear here within 30 seconds.
          </p>
        </div>
      ) : (
        <div className="rounded-ds-008 border border-silver-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.templates.map((t) => (
                <TableRow key={t.template_id}>
                  <TableCell className="font-medium">{t.title}</TableCell>
                  <TableCell className="text-xs text-carbon-500">
                    {TYPE_LABEL[t.type] ?? t.type}
                  </TableCell>
                  <TableCell className="text-xs text-carbon-500">
                    {orgNodes?.find((n) => n.id === String(t.customer_id))?.name ?? t.customer_id}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_BADGE[t.status]}>
                      {statusLabel(t.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-carbon-500">
                    {t.modified_on ? formatDate(t.modified_on) : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="secondary" size="sm" asChild>
                      <Link to={`/templates/${t.template_id}/review`}>
                        <Eye className="h-4 w-4 mr-1" />
                        Review
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
